import * as React from 'react';
import MDialog from '../../../components/controls/MDialog';
import { Typography, Stack, Divider, Box, Button, LinearProgress, Link } from '@mui/material';
import { formatDateTime } from '../../../components/ex_dayjs';
import RenderControls, { ControlConfig } from '../../../components/ra-list/RenderControls';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { useNotify } from 'react-admin';
import { MTypography } from './MTypography';
import AutocompleteConfirm from './AutocompleteConfirm';
import { Cached } from '@mui/icons-material';
import LoadingButton from '../../../components/controls/LoadingButton';
import type { IData } from '../../../types';

interface MappingPopupProps {
    data: IData;
    dataProvider: IData;
    handleClose: (event?: IData | null, dialogName?: string, action?: string) => void;
    reloadPage: () => void;
    smMarket: number;
}

const TemporarilyValue = 'Temporarily Unmap';

const MappingPopup = ({ data, dataProvider, handleClose, reloadPage, smMarket }: MappingPopupProps) => {
    const { m_isOpen, m_item, m_events, m_isLoading } = data;
    const notify = useNotify();
    const [selectionModel, setSelectionModel] = React.useState<(string | number)[]>([]);
    const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 's_total_score', sort: 'desc' }]);
    const [rows, setRows] = React.useState<IData[]>([]);
    const [state, setState] = React.useState({
        title: '',
        mappingBtn: 'Mapping!',
        skipBtn: 'skip pricer',
        temporarilyBtn: TemporarilyValue,
        mapTo: {} as IData,
        isProcessing: false, // process call api to apply
        isSelection: false,
        isSkipPricer: false,
        isTemporarily: false,
        c_isConfirm: false, // OPen dialog confirm
    });

    React.useEffect(() => {
        setSelectionModel([]);
        if (m_item) {
            let isMapped = m_item.mapped === 1;
            let title = `#${m_item.id}`;
            if (m_item.smEventID) title += ` | Portal EventID: ${m_item.smEventID.slice(0, m_item.smEventID.length - 2)}`;
            setState((prev) => ({
                ...prev, title,
                isSelection: isMapped,
                mappingBtn: isMapped ? 'Unmap!' : 'Mapping!',
                mapTo: {
                    url: m_item.smUrl,
                    name: m_item.smEvent,
                    venue: m_item.smVenue,
                    date: m_item.smDateTime,
                    city: m_item.smCity,
                    state: m_item.smState
                },
                temporarilyBtn: m_item.statusCode === TemporarilyValue ? `Turn-off ${TemporarilyValue}` : TemporarilyValue
            }));
        }
    }, [m_item]);

    React.useEffect(() => {
        setRows(m_events);
        // Focus first row
        if (m_events && m_events.length) {
            let idSelected = m_events.find((x: IData) => x.sm_market_id === smMarket)?.sm_original_id;
            const intervalId = setInterval(() => {
                let firstRow: Element | null = null;
                if (idSelected) firstRow = document.querySelector(`.MuiDataGrid-root [data-id="${idSelected}"]`);
                if (!firstRow) firstRow = document.querySelectorAll('.MuiDataGrid-root [data-id]')?.[0];
                if (firstRow) {
                    (firstRow as HTMLElement).click();
                    clearInterval(intervalId);
                }
            }, 100);
        }
    }, [m_events]);

    const columnDefs = [
        { field: 'sm_market', headerName: 'Exchange', width: 100 },
        { field: 'sm_original_id', headerName: 'SM EventID', width: 100 },
        {
            field: 'sm_event_name', headerName: 'Event', width: 350, renderCell: (record: IData) => (
                <Link underline="hover" href={record.row.sm_event_url} target="_blank">
                    {record.value}
                </Link>
            )
        },
        { field: 'sm_event_venue', headerName: 'Venue', width: 300 },
        { field: 'sm_event_city', headerName: 'City', width: 200 },
        { field: 'sm_event_state', headerName: 'State', width: 100 },
        { field: 'sm_event_date', headerName: 'Event date', width: 200, valueFormatter: (value: any) => formatDateTime(value) },
        { field: 's_total_score', headerName: 'Score', width: 100 },
    ];

    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
    };

    const handleSelectionModelChange = (newSelection: (string | number)[]) => {
        setSelectionModel(newSelection);
        // selectMapto
        if (m_item.mapped !== 1) {
            let dataModel = m_events.find((x: IData) => newSelection.some(y => y === x.sm_original_id));
            if (dataModel) {
                setState((prev) => ({
                    ...prev, mapTo: {
                        url: dataModel.sm_event_url,
                        name: dataModel.sm_event_name,
                        venue: dataModel.sm_event_venue,
                        date: dataModel.sm_event_date,
                        city: dataModel.sm_event_city,
                        state: dataModel.sm_event_state,
                    }
                }));
            }
        }
        // Mapped then allow skip/enable pricer
        else if (m_item.mapped === 1) {
            let dataModel = m_events.filter((x: IData) => newSelection.some(y => y === x.sm_original_id));
            if (dataModel.length) {
                let isEnable = dataModel.every((x: IData) => x.skip_auto_pricer === true);
                setState((prev) => ({ ...prev, skipBtn: isEnable ? 'enable pricer' : 'skip pricer' }));
            }
        }
    };

    const closeConfirm = () => setState((prev) => ({ ...prev, c_isConfirm: false }));
    const closeProcessing = () => setState((prev) => ({ ...prev, isProcessing: false }));

    const controls = [
        { component: 'number', name: 'sm_original_id', label: 'SM Event ID', xs: 2 },
        { component: 'text', name: 'sm_event_name', label: 'Event', xs: 5 },
        { component: 'text', name: 'sm_event_venue', label: 'Venue', xs: 5 }
    ] as ControlConfig[];

    function handleSearch (data: IData) {
        let isChanged = controls.some((m) => data[m.name]);
        let newData = m_events.filter((x: IData) => {
            for (let control of controls) {
                let isValid = true;
                if (data[control.name]) {
                    if (control.component === 'text')
                        isValid = x[control.name].toLowerCase().includes(data[control.name].toLowerCase());
                    else
                        isValid = x[control.name] === data[control.name];
                }
                if (!isValid) return false;
            }
            return true;
        });
        if (isChanged) setRows(newData);
        else setRows(m_events);
    }

    function handleMapping (reason: string) {
        let isMapped = m_item.mapped === 1;
        let dataRequest: IData[] = [];
        dataProvider.currentUser().then((user: IData) => {
            if (user.email) {
                if (isMapped) { // For unMapping
                    let dataModel = m_events.filter((x: IData) => selectionModel.some(y => y === x.sm_original_id));
                    dataModel.forEach((e: IData) => {
                        if (e.mapped) {
                            let d: IData = {};
                            d.EventID = m_item.id;
                            d.sm_event_id = e.sm_event_id;
                            d.sm_event_date = new Date(e.sm_event_date);
                            d.sm_market_id = e.sm_market_id;
                            d.ReasonTags = reason;
                            d.Mapped = isMapped;
                            e.Mapped_By = user.email;
                            d.json = getDataMap(e);
                            dataRequest.push(d);
                        }
                    });
                } else { // For Mapping
                    let dataMap = m_events.find((x: IData) => selectionModel.some(y => y === x.sm_original_id));
                    dataMap.EventID = m_item.id;
                    dataMap.sm_event_date = new Date(dataMap.sm_event_date);
                    dataMap.Mapped = isMapped;
                    dataMap.Mapped_By = user.email;
                    if (dataMap.mapped) dataMap.sm_event_id = m_item.smEventID;
                    dataMap.json = getDataMap(dataMap);
                    dataRequest.push(dataMap);
                }

                if (dataRequest.length) {
                    setState((prev) => ({ ...prev, isProcessing: true }));
                    dataProvider.postData('ListingsMapping', 'MappingEvent', dataRequest)
                        .then((response: IData) => {
                            closeProcessing();
                            if (response && response.data) {
                                if (response.data === true) {
                                    notify(`The event '${m_item.name}' is ${m_item.mapped === 1 ? 'Unmapped' : 'Mapped'}!!! `, { type: 'success' });
                                    handleClose(null, 'm_isOpen', 'mapping'); // Pass action to reload page after close dialog in QueueUploadPage
                                    reloadPage();
                                } else notify(`${response.data}`, { type: 'error' });
                            }
                        });
                } else {
                    notify('Please select the mapped events to unmap!!!', { type: 'warning' });
                }
            }
        });

        function getDataMap (dmap: IData) {
            return JSON.stringify({
                Name: m_item.name,
                DateTime: m_item.dateTime,
                Venue: m_item.venue,
                City: m_item.city,
                State: m_item.state,
                SmName: dmap.sm_event_name,
                SmDateTime: dmap.sm_event_date,
                SmVenue: dmap.sm_event_venue,
                SmCity: dmap.sm_event_city,
                SmState: dmap.sm_event_state,
                Score: dmap.s_total_score,
                MarketId: dmap.sm_market_id,
                Mapped_By: dmap.Mapped_By,
                Mapped_Time: new Date(),
                S_Event: dmap.s_Event,
                S_Venue: dmap.s_Venue,
                S_City: dmap.s_City,
                S_State: dmap.s_State,
                S_DateTime: dmap.s_DateTime
            });
        }
    }

    function handleSkipPricer () {
        let isMapped = m_item.mapped === 1;
        let isSkip = state.skipBtn === 'skip pricer';
        let dataRequest: IData[] = [];
        if (isMapped) { // For skip pricer
            let dataModel = m_events.filter((x: IData) => x.mapped && selectionModel.some(y => y === x.sm_original_id));
            dataModel.forEach((e: IData) => {
                let d: IData = {};
                d.EventID = m_item.id;
                d.sm_event_id = e.sm_event_id;
                d.sm_event_date = new Date(e.sm_event_date);
                d.sm_market_id = e.sm_market_id;
                d.Mapped = isMapped;
                dataRequest.push(d);
            });
        }
        if (dataRequest.length) {
            setState((prev) => ({ ...prev, isProcessing: true }));
            dataProvider.postData('ListingsMapping', `SkipPricers?isSkip=${isSkip}`, dataRequest)
                .then((response: IData) => {
                    closeProcessing();
                    if (response && response.data) {
                        if (response.data >= 0) {
                            notify(`The event '${m_item.name}' has been ${isSkip ? 'skipped' : 'enabled'} (${response.data})!!! `, { type: 'success' });
                            handleClose(null, 'm_isOpen');
                            reloadPage();
                        } else notify(`${response.data}`, { type: 'error' });
                    }
                });
        } else {
            notify('Please select the mapped events to skip!!!', { type: 'warning' });
        }
    }

    const handleTemporarily = () => {
        setState((prev) => ({ ...prev, isProcessing: true }));
        dataProvider.currentUser().then((user: IData) => {
            if (user.email) {
                let isTemp = m_item.statusCode === TemporarilyValue;
                let method = isTemp ? 'TurnOffTemporarily' : 'UnmapEvents';
                let params = isTemp ? m_item.id : [{
                    eventId: m_item.id,
                    by: user.email,
                    reasonTags: 'Temporarily Unmap'
                }];
                dataProvider.postData('ListingsMapping', method, params)
                    .then((response: IData) => {
                        closeProcessing();
                        if (response && response.data) {
                            if (response.data === true) {
                                notify(`#${m_item.id} has been ${isTemp ? 'turn-off ' : ''}temporarily unmmaped!!!`, { type: 'success' });
                                handleClose(null, 'm_isOpen', 'unmap'); // Pass action to reload page after close dialog in QueueUploadPage
                                reloadPage();
                            } else notify(`${response.data}`, { type: 'error' });
                        }
                    })
                    .catch((error: IData) => notify(`Error: ${error}`, { type: 'error' }));
            }
        });
    };

    async function refreshListings () {
        let response = await dataProvider.postData('ListingsMapping', `RefreshListings?eventId=${m_item.id}`);
        if (response) {
            if (response.data.success)
                notify(`Listings for event '${m_item.name}' have been refreshed!`, { type: 'success' });
            else
                notify(response.data.message, { type: 'error' });
        }
    }

    return (
        <>
            <MDialog name='m_isOpen'
                open={m_isOpen}
                title={state.title}
                handleClose={handleClose}
                msgLoading={`${state.isTemporarily ? TemporarilyValue : (state.isSkipPricer ? (state.skipBtn === 'skip pricer' ? 'Skipping' : 'Enabling') : m_item.mapped === 1 ? 'Unmapping' : 'Mapping')}`}
                disabled={state.isProcessing}
                maxWidth="xl"
            >
                <Typography variant="h4">You're mapping</Typography>
                <Divider orientation="horizontal" flexItem style={{ paddingTop: 5 }} />
                <Stack
                    direction="row"
                    divider={<Divider orientation="vertical" flexItem />}
                    spacing={1}
                    style={{ paddingTop: 10 }}
                >
                    <MTypography label='Event' name={m_item.name} href={m_item.url} />
                    <MTypography label='Venue' name={m_item.venue} />
                    <MTypography label='Date' datetime={m_item.dateTime} />
                    <MTypography label='City' name={m_item.city} />
                    <MTypography label='State' name={m_item.state} />
                </Stack>
                <Stack
                    direction="row"
                    divider={<Divider orientation="vertical" flexItem />}
                    spacing={1}
                    style={{ paddingTop: 10 }}
                >
                    <MTypography label='Map to' name={state.mapTo.name} href={state.mapTo.url} />
                    <MTypography label='Venue' name={state.mapTo.venue} />
                    <MTypography label='Date' datetime={state.mapTo.date} />
                    <MTypography label='City' name={state.mapTo.city} />
                    <MTypography label='State' name={state.mapTo.state} />
                </Stack>
                <RenderControls
                    filterInline={false}
                    onSearch={handleSearch}
                    positionActions='start'
                    sizeFilter={'normal'}
                    actionsStyle={{ paddingTop: 5, paddingBottom: 5 }}
                    actions={
                        <>
                            <Button size='medium'
                                variant="outlined"
                                sx={m_item.mapped !== 1 ?
                                    { color: '#4cae4c', borderColor: '#4cae4c' } :
                                    { color: '#d9534f', borderColor: '#d9534f' }
                                }
                                onClick={() => {
                                    if (selectionModel.length) setState((prev) => ({ ...prev, c_isConfirm: true, isSkipPricer: false }));
                                    else notify(`Please select event to ${m_item.mapped === 1 ? 'unmap' : 'mapping'}!!!`, { type: 'warning' });
                                }}>
                                {state.mappingBtn}
                            </Button>
                            {m_item.mapped === 1 && (
                                <>
                                    <Button size='medium'
                                        variant="outlined"
                                        sx={m_item.mapped !== 1 ?
                                            { color: '#4cae4c', borderColor: '#4cae4c' } :
                                            { color: '#d9534f', borderColor: '#d9534f' }
                                        }
                                        onClick={() => {
                                            if (selectionModel.length) setState((prev) => ({ ...prev, c_isConfirm: true, isSkipPricer: true }));
                                            else notify(`Please select event to ${state.skipBtn}!!!`, { type: 'warning' });
                                        }}>
                                        {state.skipBtn}
                                    </Button>
                                    <Button size='medium'
                                        variant="outlined" color={m_item.statusCode === TemporarilyValue ? 'error' : 'warning'}
                                        onClick={() => setState((prev) => ({ ...prev, c_isConfirm: true, isTemporarily: true }))}>
                                        {state.temporarilyBtn}
                                    </Button>
                                </>
                            )}
                            <LoadingButton size='medium'
                                startIcon={<Cached />}
                                variant="outlined"
                                onClick={refreshListings}>
                                Refresh listings
                            </LoadingButton>
                        </>
                    }
                    controls={controls} />
                {m_isLoading && <LinearProgress color='secondary' />}
                <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                        key={state.isSelection ? 'with-checkbox' : 'without-checkbox'}
                        rows={rows}
                        columns={columnDefs}
                        getRowId={(r) => r.sm_original_id}
                        sortModel={sortModel}
                        autoPageSize={true}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                },
                            },
                        }}
                        rowSelectionModel={selectionModel}
                        checkboxSelection={state.isSelection}
                        onRowSelectionModelChange={handleSelectionModelChange as any}
                        onSortModelChange={handleSortModelChange}
                    />
                </Box>
            </MDialog>
            <AutocompleteConfirm
                isOpen={state.c_isConfirm}
                title={`Are you sure apply(${state.isTemporarily ? TemporarilyValue : (state.isSkipPricer ? state.skipBtn : m_item.mapped === 1 ? 'unmap' : 'mapping')}) for this event(s)?`}
                showReason={m_item.mapped === 1 && !state.isSkipPricer && !state.isTemporarily}
                onClose={closeConfirm}
                onConfirm={(reason) => {
                    setState((prev) => ({ ...prev, c_isConfirm: false }));
                    if (state.isTemporarily) handleTemporarily();
                    else if (state.isSkipPricer) handleSkipPricer();
                    else handleMapping(reason);
                }}
            />
        </>
    );
};

export default MappingPopup;
